import { useState, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import DataTable from '../../components/common/DataTable';
import Pagination from '../../components/common/Pagination';
import portalDataService from '../../services/portalDataService';
import usePortalList from '../hooks/usePortalList';
import PortalFilterBar from '../components/PortalFilterBar';
import PortalStatusBadge from '../components/PortalStatusBadge';
import PortalInquiryViewModal from '../components/PortalInquiryViewModal';
import PortalInquiryCreateModal from '../components/PortalInquiryCreateModal';
import { formatDate, formatMoney, INQUIRY_STATUSES } from '../portalUtils';
import { UI_COLORS, GRADIENTS } from '../../utils/colors';

const PAGE_SIZE = 20;

const PortalInquiriesPage = () => {
  const { t } = useTranslation();
  const s = t('portal.inquiries', { returnObjects: true });
  const c = t('portal.common', { returnObjects: true });
  const rawStatusLabels = t('portal.inquiries.statuses', { returnObjects: true });
  const statusLabels = (rawStatusLabels && typeof rawStatusLabels === 'object') ? rawStatusLabels : {};

  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const fetcher = useCallback((params) => portalDataService.getInquiries(params), []);
  const filters = useMemo(() => ({ status }), [status]);
  const { items, total, totalPages, loading, reload } = usePortalList(fetcher, filters, page, PAGE_SIZE);

  // Client-side search over the current page (inquiry # / status).
  const rows = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((o) =>
      String(o.id || '').toLowerCase().includes(q) ||
      (o.status || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  const statusOptions = useMemo(
    () => [{ value: '', label: c?.allStatuses || 'All statuses' }, ...INQUIRY_STATUSES.map((v) => ({ value: v, label: statusLabels[v] || v }))],
    [c, statusLabels]
  );

  const detailReqRef = useRef(0);
  const openDetail = useCallback(async (row) => {
    const reqId = ++detailReqRef.current;
    setSelected(row);
    setDetail(null);
    setDetailLoading(true);
    const res = await portalDataService.getInquiry(row.id);
    if (reqId !== detailReqRef.current) return;
    setDetail(res.success ? res.data : row);
    setDetailLoading(false);
  }, []);

  const columns = useMemo(() => [
    { id: 'id', label: s?.columns?.id || 'Inquiry #', renderCell: (o) => <span className="font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>#{o.id}</span> },
    { id: 'created_at', label: s?.columns?.created || 'Created', renderCell: (o) => formatDate(o.created_at) },
    { id: 'items_count', label: s?.columns?.items || 'Items', align: 'center', renderCell: (o) => (o.items_count != null ? o.items_count : '—') },
    { id: 'expected_delivery_date', label: s?.columns?.expectedDelivery || 'Expected Delivery', renderCell: (o) => formatDate(o.expected_delivery_date) },
    { id: 'total', label: s?.columns?.total || 'Total', align: 'right', renderCell: (o) => (Number(o.total) > 0 ? formatMoney(o.total) : <span style={{ color: UI_COLORS.TEXT.MUTED }}>{s?.pendingQuote || 'Pending quotation'}</span>) },
    { id: 'status', label: s?.columns?.status || 'Status', renderCell: (o) => <PortalStatusBadge status={o.status} labels={statusLabels} /> },
    { id: 'actions', label: c?.actions || 'Actions', align: 'center', renderCell: (o) => (
      <button type="button" onClick={() => openDetail(o)} className="px-3 py-1 rounded-lg text-xs font-semibold" style={{ color: UI_COLORS.BRAND.TERTIARY, backgroundColor: 'rgba(24,154,180,0.1)' }}>
        {c?.view || 'View'}
      </button>
    ) },
  ], [s, c, statusLabels, openDetail]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{s?.title || 'My Inquiries'}</h1>
      </div>

      <PortalFilterBar
        statusOptions={statusOptions}
        status={status}
        onStatusChange={(v) => { setStatus(v); setPage(1); }}
        showDates={false}
        search={search} onSearchChange={setSearch}
        rightSlot={(
          <button type="button" onClick={() => setCreateOpen(true)} className="px-4 py-2 rounded-lg text-sm font-semibold" style={{ background: GRADIENTS.PRIMARY, color: UI_COLORS.BUTTONS.PRIMARY.text }}>
            + {s?.newInquiry || 'New inquiry'}
          </button>
        )}
      />

      <DataTable
        columns={columns}
        data={rows}
        loading={loading}
        keyField="id"
        enablePagination={false}
        emptyState={{ title: s?.empty?.title || 'No inquiries', message: s?.empty?.message || 'Raise an inquiry to get a quote.' }}
      />

      {!search.trim() && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalCount={total}
          onPageChange={setPage}
          loading={loading}
          totalLabel={s?.totalLabel || 'total inquiries'}
          pageSize={PAGE_SIZE}
        />
      )}

      <PortalInquiryViewModal isOpen={!!selected} onClose={() => setSelected(null)} inquiry={detail} loading={detailLoading} statusLabels={statusLabels} />
      <PortalInquiryCreateModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { if (page === 1) reload(); else setPage(1); }}
      />
    </div>
  );
};

export default PortalInquiriesPage;
